import React, { useEffect, useState } from 'react';
import { HiMenuAlt4, HiX } from 'react-icons/hi';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import './Navbar.scss';

const links = [
  ['home', 'Home'],
  ['about', 'About'],
  ['work', 'Projects'],
  ['research', 'Research'],
  ['skills', 'Experience'],
  ['contact', 'Contact'],
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrollProgress, setScrollProgress] = useState(0);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const sections = links
      .map(([id]) => document.getElementById(id))
      .filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: '-25% 0px -60%', threshold: [0, 0.2, 0.5] },
    );

    sections.forEach((section) => observer.observe(section));

    const updateProgress = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(scrollable > 0 ? window.scrollY / scrollable : 0);
    };

    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateProgress);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <header className="site-header">
      <div className="scroll-progress" style={{ transform: `scaleX(${scrollProgress})` }} />
      <nav className="app__navbar" aria-label="Primary navigation">
        <a className="app__navbar-logo" href="#home" aria-label="knowaminul, home">
          <strong>knowaminul</strong><span aria-hidden="true" />
        </a>

        <ul className="app__navbar-links">
          {links.map(([id, label]) => (
            <li key={id}>
              <a className={activeSection === id ? 'is-active' : ''} href={`#${id}`}>
                {label}
              </a>
            </li>
          ))}
        </ul>

        <a className="navbar-cta" href="/CV_MdAminulIslam.pdf" target="_blank" rel="noreferrer">
          Academic CV <span aria-hidden="true">↗</span>
        </a>

        <button
          className="app__navbar-menu-button"
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-navigation"
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <HiX /> : <HiMenuAlt4 />}
        </button>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              id="mobile-navigation"
              className="app__navbar-menu"
              initial={reduceMotion ? false : { x: '100%' }}
              animate={{ x: 0 }}
              exit={reduceMotion ? undefined : { x: '100%' }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="eyebrow">Navigate</p>
              <ul>
                {links.map(([id, label], index) => (
                  <li key={id}>
                    <a href={`#${id}`} onClick={() => setMenuOpen(false)}>
                      <span>0{index + 1}</span>{label}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mobile-nav-meta">
                <a href="/CV_MdAminulIslam.pdf" target="_blank" rel="noreferrer">Download academic CV ↗</a>
                <a className="mobile-nav-email" href="mailto:aminul.csebu@gmail.com">
                  aminul.csebu@gmail.com
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Navbar;
